import { AlertTriangle, ScanLine } from 'lucide-react';
import { Badge, ErrorState, LoadingState } from '@/components/ui';
import type { PayerNotice, ScanResult } from '@/features/queue/types';
import { AppCheckInSummary, LateNoticeNote } from './AppCheckInSummary';
import { SCAN_RESULT_LABELS, VIA_LABELS } from './app-arrival-format';
import styles from './AppArrival.module.css';

/**
 * The last counter scan (POST /queue/scan): how the patient was identified
 * (`via`: app pass or counter token), the ticket, what to tell the patient,
 * the `payerNotice` when the app-chosen cover no longer pays, and what the
 * patient sent from the app (late notice, day-before check-in).
 */
export function ScanResultPanel({
  pending,
  error,
  result,
}: {
  pending: boolean;
  error: unknown;
  result: ScanResult | null;
}) {
  return (
    <div className={styles.panel} aria-live="polite">
      <p className="eyebrow">Scan result</p>
      {pending ? (
        <LoadingState label="Checking the pass…" />
      ) : error ? (
        <ErrorState error={error} />
      ) : !result ? (
        <p className={styles.sub}>
          No scan yet. The result, the ticket and anything the patient sent from
          the app show here.
        </p>
      ) : (
        <ScanResultBody r={result} />
      )}
    </div>
  );
}

function ScanResultBody({ r }: { r: ScanResult }) {
  const appt = r.appointment;
  const ticketNo = r.ticket?.number ?? appt?.queueTicket?.number;
  return (
    <section className={styles.box} aria-label="Scan result details">
      <div className={styles.head}>
        <span className={styles.name}>{r.patient.fullName}</span>
        <Badge tone={r.result === 'checked_in' ? 'success' : 'info'}>
          {SCAN_RESULT_LABELS[r.result]}
        </Badge>
        {r.via && (
          <Badge icon={<ScanLine size={12} aria-hidden="true" />}>
            {`Via ${VIA_LABELS[r.via] ?? r.via}`}
          </Badge>
        )}
      </div>
      {ticketNo !== undefined && (
        <p className={styles.sub}>Ticket {ticketNo}</p>
      )}
      {appt?.arrivedLate && (
        <Badge tone="warning">Arrived late: walk-in priority</Badge>
      )}
      <p>{r.message}</p>
      <PayerNoticeNote notice={r.payerNotice ?? appt?.payerNotice ?? null} />
      {appt && (
        <>
          <LateNoticeNote notice={appt.lateNotice} />
          <AppCheckInSummary checkIn={appt.appCheckIn} showEmpty />
        </>
      )}
    </section>
  );
}

/** RL-941 `payerNotice` (APP_COVERAGE_NOT_USABLE): the visit is self-pay for now. */
export function PayerNoticeNote({
  notice,
}: {
  notice: PayerNotice | null | undefined;
}) {
  if (!notice) return null;
  return (
    <div className={styles.payer} role="alert">
      <strong>
        <AlertTriangle size={14} aria-hidden="true" />
        Who pays: ask the patient
      </strong>
      <span>{notice.message}</span>
      <span className={styles.code}>{notice.code}</span>
    </div>
  );
}
